import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { games as gamesApi } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import GameEditorDialog from "../components/GameEditorDialog";
import {
  Gamepad2,
  Plus,
  Play,
  Share2,
  Pencil,
  Trash2,
  Trophy,
  ListChecks,
  History,
  Radio,
} from "lucide-react";

export default function GamesPage() {
  const { user } = useApp();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const canHost = ["admin", "direccion"].includes(user?.role);

  const [tab, setTab] = useState("games");
  const [items, setItems] = useState([]);
  const [matches, setMatches] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => gamesApi.list().then(setItems).catch(console.error);
  const loadMatches = () => gamesApi.matches().then(setMatches).catch(console.error);

  useEffect(() => { load(); loadMatches(); }, []);

  const openNew = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = async (g) => {
    const full = await gamesApi.get(g.id).catch(() => g);
    setEditing(full);
    setEditorOpen(true);
  };
  const onDelete = async (g) => {
    if (!window.confirm(t("games.confirm_delete", { title: g.title }))) return;
    await gamesApi.remove(g.id);
    toast.success(t("games.deleted"));
    load();
  };
  const copyShare = async (g) => {
    const url = `${window.location.origin}/play/${g.share_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("games.link_copied"));
    } catch {
      toast.message(url);
    }
  };

  const winnerCount = useMemo(() => matches.length, [matches]);

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8" data-testid="games-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold flex items-center gap-2">
            <Gamepad2 size={15} /> {t("games.module")}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Canales Dijeron</h1>
          <p className="text-stone-500 mt-1 text-sm">{t("games.subtitle")}</p>
        </div>
        {isAdmin && (
          <Button onClick={openNew} className="brand-button" data-testid="new-game-btn">
            <Plus size={16} className="mr-1.5" /> {t("games.new_game")}
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mt-6 border-b border-stone-200">
        <TabBtn active={tab === "games"} onClick={() => setTab("games")} icon={ListChecks} label={t("games.tab_games")} testid="tab-games" />
        <TabBtn active={tab === "history"} onClick={() => setTab("history")} icon={History} label={`${t("games.tab_history")} (${winnerCount})`} testid="tab-history" />
      </div>

      {/* Games grid */}
      {tab === "games" && (
        <div className="mt-6">
          {items.length === 0 ? (
            <EmptyState isAdmin={isAdmin} onNew={openNew} t={t} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((g) => (
                <Card key={g.id} className="p-5 flex flex-col" data-testid={`game-set-card-${g.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display text-lg font-bold leading-tight">{g.title}</h3>
                    {g.active === false && <span className="pill text-[10px] bg-stone-100 text-stone-500">{t("games.inactive")}</span>}
                  </div>
                  {g.description ? <p className="text-sm text-stone-500 mt-1 line-clamp-2">{g.description}</p> : null}
                  <div className="text-xs text-stone-400 mt-2">{(g.questions || []).length} {t("games.questions")}</div>

                  <div className="mt-4 pt-4 border-t border-stone-100 space-y-2">
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => navigate(`/games/${g.id}/play`)}
                        className="flex-1 bg-[#0A1329] hover:bg-[#152244] text-white"
                        data-testid={`play-game-${g.id}`}
                      >
                        <Play size={15} className="mr-1.5" /> {t("games.play")}
                      </Button>
                      {canHost && (
                        <Button
                          onClick={() => navigate(`/games/${g.id}/host`)}
                          className="flex-1 bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-[#0A1329] font-bold hover:brightness-110"
                          data-testid={`host-game-${g.id}`}
                        >
                          <Radio size={15} className="mr-1.5" /> {t("games.live.play_live")}
                        </Button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => copyShare(g)} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border border-stone-200 text-sm text-stone-500 hover:text-[#1E3A8A] hover:border-[#1E3A8A]" title={t("games.copy_link")} data-testid={`share-game-${g.id}`}>
                        <Share2 size={15} /> {t("games.copy_link")}
                      </button>
                      {isAdmin && (
                        <button onClick={() => openEdit(g)} className="p-2.5 rounded-lg border border-stone-200 text-stone-500 hover:text-[#1E3A8A] hover:border-[#1E3A8A]" title={t("common.edit")} data-testid={`edit-game-${g.id}`}>
                          <Pencil size={15} />
                        </button>
                      )}
                      {isAdmin && (
                        <button onClick={() => onDelete(g)} className="p-2.5 rounded-lg border border-stone-200 text-stone-500 hover:text-red-600 hover:border-red-300" title={t("common.delete")} data-testid={`delete-game-${g.id}`}>
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* History */}
      {tab === "history" && (
        <div className="mt-6" data-testid="games-history">
          {matches.length === 0 ? (
            <p className="text-stone-500 text-sm">{t("games.no_matches")}</p>
          ) : (
            <Card className="p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-3">{t("games.col_game")}</th>
                    <th className="text-left px-4 py-3">{t("games.col_result")}</th>
                    <th className="text-left px-4 py-3">{t("games.col_winner")}</th>
                    <th className="text-left px-4 py-3 hidden md:table-cell">{t("games.col_host")}</th>
                    <th className="text-left px-4 py-3 hidden md:table-cell">{t("games.col_date")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {matches.map((m) => (
                    <tr key={m.id} className="hover:bg-stone-50" data-testid={`match-row-${m.id}`}>
                      <td className="px-4 py-3 font-medium">{m.game_title || "—"}</td>
                      <td className="px-4 py-3 text-stone-600">
                        {m.team_a_name} <b>{m.team_a_score}</b> · {m.team_b_name} <b>{m.team_b_score}</b>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 font-semibold text-[#6B3E2E]">
                          <Trophy size={13} /> {m.winner}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-stone-500 hidden md:table-cell">{m.host || "—"}</td>
                      <td className="px-4 py-3 text-stone-500 hidden md:table-cell">{(m.played_at || "").slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}

      <GameEditorDialog open={editorOpen} onOpenChange={setEditorOpen} game={editing} onSaved={() => { load(); }} />
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
        active ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-stone-500 hover:text-stone-800"
      }`}
    >
      <Icon size={15} /> {label}
    </button>
  );
}

function EmptyState({ isAdmin, onNew, t }) {
  return (
    <Card className="p-10 text-center">
      <div className="w-14 h-14 rounded-2xl bg-[#E5EBFA] text-[#1E3A8A] grid place-items-center mx-auto">
        <Gamepad2 size={26} />
      </div>
      <h3 className="font-display text-xl font-bold mt-4">{t("games.empty_title")}</h3>
      <p className="text-stone-500 text-sm mt-1 max-w-md mx-auto">{t("games.empty_desc")}</p>
      {isAdmin && (
        <Button onClick={onNew} className="brand-button mt-5" data-testid="empty-new-game-btn">
          <Plus size={16} className="mr-1.5" /> {t("games.new_game")}
        </Button>
      )}
    </Card>
  );
}
